// Search descriptions for /modules/:key are derived only from the reviewed module
// catalogue. Keep this helper pure so runtime metadata, schemas and prerendered pages
// all receive the same sentence without maintaining a second copy registry.

export const MODULE_SEO_DESCRIPTION_MAX_LENGTH = 155;

const TRAILING_FUNCTION_WORDS = new Set([
  "a", "an", "and", "are", "as", "at", "be", "been", "being", "but", "by",
  "for", "from", "if", "in", "into", "is", "of", "on", "or", "so", "than",
  "that", "the", "then", "through", "to", "was", "were", "when", "where",
  "which", "while", "who", "with", "without",
]);

const SUPPORTING_SENTENCE = "See its workflow, users, outputs and rollout requirements in Vidyapeeth360.";

function normalizeCopy(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .replace(/\s+([,.;:!?])/g, (_match, punctuation) => punctuation)
    .replace(/\.{2,}/g, ".")
    .trim();
}

function ensureTerminalPunctuation(value) {
  const text = normalizeCopy(value).replace(/[\s,;:—–-]+$/g, "");
  return text && !/[.!?]$/.test(text) ? `${text}.` : text;
}

function stripStrandedEnding(value) {
  const words = normalizeCopy(value).replace(/[.!?]+$/g, "").split(" ").filter(Boolean);
  while (words.length > 1) {
    const finalWord = words[words.length - 1].toLowerCase().replace(/[^a-z]/g, "");
    if (!TRAILING_FUNCTION_WORDS.has(finalWord)) break;
    words.pop();
  }
  return words.join(" ").replace(/[\s,;:—–-]+$/g, "");
}

function completeSentencesWithin(text, maxLength) {
  const sentences = normalizeCopy(text).match(/[^.!?]+[.!?]+(?:["')\]]+)?/g) || [];
  let result = "";
  for (const sentence of sentences) {
    const candidate = normalizeCopy(`${result} ${sentence}`);
    if (candidate.length > maxLength) break;
    result = candidate;
  }
  return result;
}

function conciseSentence(text, maxLength) {
  const normalized = ensureTerminalPunctuation(text);
  if (normalized.length <= maxLength) return normalized;

  // Prefer a complete authored sentence. Only a single overlong sentence reaches the
  // boundary fallback below, which preserves whole words and removes weak clause tails.
  const complete = completeSentencesWithin(normalized, maxLength);
  if (complete) return ensureTerminalPunctuation(complete);

  const punctuationBudget = Math.max(1, maxLength - 1);
  const prefix = normalized.slice(0, punctuationBudget + 1);
  const clauseBoundaries = ["; ", " — ", " – ", ": ", ", "];
  const clauseBoundary = Math.max(...clauseBoundaries.map((separator) => prefix.lastIndexOf(separator)));
  const minimumUsefulBoundary = Math.min(72, Math.floor(maxLength * 0.58));
  const wordBoundary = prefix.lastIndexOf(" ");
  const boundary = clauseBoundary >= minimumUsefulBoundary ? clauseBoundary : wordBoundary;
  const authoredPrefix = normalized.slice(0, Math.max(1, boundary));
  return ensureTerminalPunctuation(stripStrandedEnding(authoredPrefix));
}

/**
 * Build one truthful search description from approved module catalogue copy.
 *
 * The module one-liner is the preferred source because it was written as the concise
 * buyer promise. The long-form `what` paragraph is only a fallback. Short summaries may
 * receive a factual page-orientation sentence; no feature claim is inferred here.
 */
export function moduleSeoDescription(mod, maxLength = MODULE_SEO_DESCRIPTION_MAX_LENGTH) {
  const safeLimit = Math.max(72, Number(maxLength) || MODULE_SEO_DESCRIPTION_MAX_LENGTH);
  const authoredSummary = normalizeCopy(mod?.oneLiner || mod?.detail?.what);
  const fallback = `Review ${normalizeCopy(mod?.name || "this module")} workflows, users, outputs and rollout requirements in Vidyapeeth360.`;
  const summary = authoredSummary || fallback;
  const sentence = conciseSentence(summary, safeLimit);
  const supported = normalizeCopy(`${sentence} ${SUPPORTING_SENTENCE}`);

  return supported.length <= safeLimit
    ? ensureTerminalPunctuation(supported)
    : conciseSentence(sentence, safeLimit);
}
